import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Backdrop, CircularProgress } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { Z_INDEX } from '@/constants/z-index';
import { dashboardQueryKeys, useDashboard } from '@/features/Dashboard/api';
import { getDashboardApiUrl } from '@/features/Dashboard/api/dashboardUrl';
import { waitForApiSync } from '@/features/Dashboard/api/mutations';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { broadcastDashboardMutation } from '@/features/Dashboard/hooks/useDashboardSync';
import type { TabInfo } from '@/features/Dashboard/pages/Edit/components/menus/DashboardTargetMenu';
import { DashboardTargetMenu } from '@/features/Dashboard/pages/Edit/components/menus/DashboardTargetMenu';
import type { DashboardTab } from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import {
  clearPendingAddAppDrawer,
  requestAddAppDrawer,
  SELECTOR_MODES
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch } from '@/hooks/redux-hooks';

interface CreateAppMenuProps {
  /** Selector container element — used as Popper anchor (bottom-end placement) */
  containerEl?: HTMLElement;
  open: boolean;
  onClose: () => void;
  selectorMode: SELECTOR_MODES;
}

const MENU_BG_COLOR_SPOT = 'primary.main';
const MENU_BG_COLOR_CURVE = 'gx.selectorForwardCurve';

const CreateAppMenu = ({ containerEl, open, onClose, selectorMode }: CreateAppMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const navigationTargetRef = useRef<{ dashboardName: string; tabName: string } | null>(null);

  const { mutate: createTab, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      tabName,
      payload
    }: {
      dashboardName: string;
      tabName: string;
      payload: DashboardTab;
    }) => {
      navigationTargetRef.current = { dashboardName, tabName };
      await httpInstances.axiosInternalInstance.post(
        getDashboardApiUrl(encodeURIComponent(dashboardName)),
        payload
      );
      await waitForApiSync();
    },
    onSuccess: async () => {
      broadcastDashboardMutation();
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
      if (navigationTargetRef.current) {
        const { dashboardName, tabName } = navigationTargetRef.current;
        navigateAndOpenDrawer(dashboardName, tabName);
        navigationTargetRef.current = null;
      }
    },
    onError: () => {
      navigationTargetRef.current = null;
    }
  });

  const navigateAndOpenDrawer = useCallback(
    (dashboardName: string, tabName: string) => {
      dispatch(clearPendingAddAppDrawer());
      navigate(`/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(tabName)}`);
      dispatch(requestAddAppDrawer());
    },
    [dispatch, navigate]
  );

  const handleSelectTab = useCallback(
    (dashboardName: string, tab: TabInfo) => {
      if (isPending) return;
      navigateAndOpenDrawer(dashboardName, tab.tabName);
      onClose();
    },
    [isPending, navigateAndOpenDrawer, onClose]
  );

  const handleCreateNewTab = useCallback(
    (dashboardName: string) => {
      if (isPending || !allDashboards) return;
      const existingTabs = (allDashboards[dashboardName] as DashboardTab | undefined) ?? {};
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);
      const payload: DashboardTab = { ...existingTabs, [newTabName]: [] };
      createTab({ dashboardName, tabName: newTabName, payload });
      onClose();
    },
    [isPending, allDashboards, createTab, onClose]
  );

  const handleCreateNewDashboard = useCallback(() => {
    if (isPending) return;
    const existingNames = Object.keys(allDashboards || {});
    const newName = getUniqueDashboardName(existingNames);
    const payload: DashboardTab = { [TAB_DEFAULT_BASE_NAME]: [] };
    createTab({ dashboardName: newName, tabName: TAB_DEFAULT_BASE_NAME, payload });
    onClose();
    // Navigation is handled in the mutation's onSuccess callback;
    // returning undefined signals DashboardTargetMenu to take no further action.
  }, [isPending, allDashboards, createTab, onClose]);

  const getDashboardTabs = useCallback(
    (dashboardName: string) => {
      const tabs = allDashboards?.[dashboardName];
      if (!tabs) return null;
      return Object.keys(tabs).map(name => ({ tabId: name, tabName: name }));
    },
    [allDashboards]
  );

  const paperBgColor =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE ? MENU_BG_COLOR_CURVE : MENU_BG_COLOR_SPOT;

  return (
    <>
      {isPending &&
        createPortal(
          <Backdrop
            open
            sx={{ zIndex: Z_INDEX.loadingBackdrop }}
          >
            <CircularProgress />
          </Backdrop>,
          document.body
        )}
      <DashboardTargetMenu
        anchorEl={containerEl ?? null}
        dashboards={Object.keys(allDashboards || {})}
        disabled={isPending}
        getTabs={getDashboardTabs}
        headerText='Select destination dashboard'
        offset={[0, 0]}
        open={open}
        paperSx={{
          backgroundColor: paperBgColor,
          width: 162,
          minWidth: 0
        }}
        placement='bottom-end'
        zIndexWhenBelow={Z_INDEX.priorityTop}
        zIndexWhenAbove={Z_INDEX.priorityLow}
        onClose={onClose}
        onCreateDashboard={handleCreateNewDashboard}
        onCreateNewTab={handleCreateNewTab}
        onSelectTab={(dashboardName, tab) => {
          handleSelectTab(dashboardName, tab);
        }}
      />
    </>
  );
};

export default CreateAppMenu;
